/**
 * What the Devices screen says about each lift, door and edge service
 * (UI round of 2026-11-02, option G): the pure half, read by the tables,
 * the phone rows, the summary line and Needs attention alike.
 *
 * Nothing here re-reads a car. `LiftColumnState` (`lift-column-state.ts`)
 * is the one reading every lift surface shares, and these functions only
 * put it into the words and columns this screen draws, so the table can
 * never disagree with the drawer or the map.
 */
import type { DoorViewModel, LiftViewModel } from '../../api/adapters';
import type { EdgeGateway } from '../../api/hooks/useGateways';
import type { DoorHealth, DoorWriteGate } from '../../api/v1-client';
import type { LiftWriteGate } from '../../api/v1';
import type { Audience } from '../../auth/roles';
import type { AttentionSeverity, SummaryFact } from '../../design-system/components/ScreenParts';
import type { StatusKind } from '../../design-system/tokens';
import { liftCardAfterMs } from '../../state/attention/lift-items';
import { doorGateStatus, armBlockedReason as doorArmBlockedReason } from './door-gate-status';
import type { LiftColumnState } from './lift-column-state';
import { writeGateStatus } from './lift-dispatch-status';
import { readLiftModeLabel, type LiftModeClass } from './lift-mode-label';
import type { LiftRobotFacts } from './lift-reading';
import { robotName } from './lift-reading';

/** Sorts "L2" before "L10", and ignores case. */
export function naturalKey(text: string): string {
  return text.toLowerCase().replace(/\d+/g, (n) => n.padStart(6, '0'));
}

/** "45 s", "1 min 40 s", "12 min", "3 h 5 min", "2 d 3 h": each step drops
 *  what stops mattering at that length. */
export function durationWords(ms: number): string {
  const totalS = Math.max(0, Math.floor(ms / 1000));
  if (totalS < 60) return `${totalS} s`;
  const totalMin = Math.floor(totalS / 60);
  if (totalMin >= 24 * 60) {
    const h = Math.floor(totalMin / 60) % 24;
    return `${Math.floor(totalMin / (24 * 60))} d${h === 0 ? '' : ` ${h} h`}`;
  }
  if (totalMin >= 60) {
    const min = totalMin % 60;
    return `${Math.floor(totalMin / 60)} h${min === 0 ? '' : ` ${min} min`}`;
  }
  const s = totalS % 60;
  if (totalMin >= 10 || s === 0) return `${totalMin} min`;
  return `${totalMin} min ${s} s`;
}

// ----------------------------------------------------------------------------
// Lifts: Mode.
// ----------------------------------------------------------------------------

export interface LiftModeCell {
  readonly status: StatusKind;
  readonly label: string;
  /** The car's own word, plus "since HH:MM" when known. */
  readonly small: string | null;
  readonly dashed: boolean;
}

const MODE_CHIP: Readonly<Record<LiftModeClass, { status: StatusKind; label: string }>> = {
  normal: { status: 'active', label: 'Normal' },
  robot: { status: 'charging', label: 'Robot' },
  staff: { status: 'warning', label: 'Staff setting' },
  fire: { status: 'error', label: 'Fire service' },
  not_in_service: { status: 'error', label: 'Out of service' },
  not_reported: { status: 'offline', label: 'Not reported' },
  unrecognised: { status: 'offline', label: 'Not known' },
};

const NOT_REPORTED: LiftModeCell = {
  ...MODE_CHIP.not_reported,
  small: null,
  dashed: true,
};

/**
 * The Mode column. The car's words are shown only while something vouches
 * for them: a lost car's last words are not its mode, and while lift
 * control is off the connector keeps sending the edge's RETAINED label
 * (`liftReading`, control off), so those are quoted only when it says its
 * telemetry is fresh.
 */
export function liftModeCell(
  row: Pick<LiftViewModel, 'hasLiveState' | 'mode' | 'session'>,
  column: Pick<LiftColumnState, 'look' | 'reading'> | undefined,
  clock: (epochMs: number) => string,
): LiftModeCell {
  if (!row.hasLiveState || !column || column.look === 'hollow') return NOT_REPORTED;
  const { reading } = column;
  const words = readLiftModeLabel(row.session?.mode_label);
  // The connector's own vocabulary says fire even from an adapter too old
  // to carry the car's words, and fire outranks everything on this row.
  if (row.mode === 'fire') {
    return { ...MODE_CHIP.fire, small: words.label, dashed: false };
  }
  if (reading.kind === 'control_off' && row.session?.telemetry_fresh !== true) {
    return { ...NOT_REPORTED, small: 'Not known while lift control is off' };
  }
  if (words.modeClass === 'not_reported' && row.mode === 'agv') {
    return { ...MODE_CHIP.robot, small: null, dashed: false };
  }
  // Armed and validated, and the car says it is offline: that one is the
  // lift, whether or not the adapter carries the car's own words.
  if (words.modeClass === 'not_reported' && reading.kind === 'lift_offline') {
    return { ...MODE_CHIP.not_in_service, small: null, dashed: false };
  }
  const since = reading.sinceEpochMs === null ? null : `since ${clock(reading.sinceEpochMs)}`;
  const small = [words.label, words.modeClass === 'normal' ? null : since]
    .filter((part): part is string => part !== null)
    .join(' · ');
  return {
    ...MODE_CHIP[words.modeClass],
    small: small === '' ? null : small,
    dashed: words.modeClass === 'not_reported',
  };
}

// ----------------------------------------------------------------------------
// Lifts: Robot, and what counts as a stuck ride.
// ----------------------------------------------------------------------------

/** Adapter `_INSIDE_PHASES`, less `held_egress`: carrying the robot. A
 *  phase this file does not know keeps the reading's own "In use by". */
const CARRYING_PHASES: ReadonlySet<string> = new Set([
  'calling_destination',
  'dest_ack',
  'dest_processed',
  'egress',
]);

const HELD_PHASE_WORDS: Readonly<Record<string, string>> = {
  held_boarding: 'boarding',
  held: 'inside',
  held_egress: 'getting out',
};

/** The part of a car's column state its Robot cell reads. */
type LiftCellColumn = Pick<LiftColumnState, 'reading' | 'facts'>;

export interface LiftRobotCell {
  readonly text: string;
  /** Past the Overview's own threshold for this kind of wait. */
  readonly stuck: boolean;
  /** The robot on this ride, if any. */
  readonly robot: string | null;
  /** How long this wait has lasted, when the car's stamps say. */
  readonly ageMs: number | null;
}

/** The earliest of the two stamps the ride carries: a wait's own stamp
 *  restarts with each step, and a ride stuck across steps is still stuck.
 *  `phase_ts` is the edge's epoch seconds; anything too small to be one is
 *  not trusted. Connector and edge clocks, as the drawer's countdown is. */
function rideStartedMs(
  row: Pick<LiftViewModel, 'session'>,
  reading: LiftColumnState['reading'],
): number | null {
  const phaseTs = row.session?.phase_ts;
  const phaseMs = typeof phaseTs === 'number' && phaseTs > 1e9 ? phaseTs * 1000 : null;
  const stamps = [reading.sinceEpochMs, phaseMs].filter((v): v is number => v !== null);
  return stamps.length === 0 ? null : Math.min(...stamps);
}

export function liftRobotCell(
  row: Pick<LiftViewModel, 'session'>,
  column: LiftCellColumn | undefined,
  wallNowMs: number,
): LiftRobotCell {
  if (!column) return { text: '—', stuck: false, robot: null, ageMs: null };
  const { reading } = column;
  const ownerId = row.session?.owner?.kind === 'robot' ? row.session.owner.id : null;
  const robot = ownerId === null ? null : robotName(ownerId);
  if ((reading.kind === 'robot_held' || reading.kind === 'robot_waiting') && robot !== null) {
    const startedMs = rideStartedMs(row, reading);
    const ageMs = startedMs === null ? null : Math.max(0, wallNowMs - startedMs);
    const words =
      reading.kind === 'robot_waiting'
        ? 'waiting to board'
        : (HELD_PHASE_WORDS[row.session?.phase ?? ''] ?? 'inside');
    const after = liftCardAfterMs(reading.kind);
    return {
      text: `${robot} ${words}${ageMs === null ? '' : ` · ${durationWords(ageMs)}`}`,
      // The Overview's cards decide when a held car or a hall wait is
      // worth a person's time; this screen agrees with them.
      stuck: after !== null && ageMs !== null && ageMs >= after,
      robot,
      ageMs,
    };
  }
  if (reading.kind === 'robot_in_use' && robot !== null) {
    const known = CARRYING_PHASES.has(row.session?.phase ?? '');
    return { text: known ? `${robot} inside` : reading.robots, stuck: false, robot, ageMs: null };
  }
  if (
    reading.kind === 'not_taking_robots' &&
    reading.mode.modeClass === 'staff' &&
    column.facts.use !== 'none'
  ) {
    return { text: 'No robots until it is back to Normal', stuck: false, robot: null, ageMs: null };
  }
  return { text: reading.robots, stuck: false, robot, ageMs: null };
}

// ----------------------------------------------------------------------------
// Lifts: the summary line.
// ----------------------------------------------------------------------------

export interface LiftSummary {
  readonly headline: string;
  readonly facts: readonly SummaryFact[];
}

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

/**
 * "2 of 2 robot lifts open · 4 guest lifts". A guest car never counts as a
 * problem (Akash, 2026-11-02): at NoMad only SL1 and PL3 take robots, and
 * "2 of 6" would read as trouble every day. A site with no building data
 * cannot tell them apart, so every car is counted.
 */
export function liftSummary(
  cars: readonly LiftRobotFacts[],
  columns: ReadonlyMap<string, Pick<LiftColumnState, 'reading'>>,
  buildingKnown: boolean,
  fireAlarm: boolean,
  /** Lifts a lift rule has blocked (`ruleBlockedLifts`): not open, and not a
   *  fault either, so they count apart from "not available". */
  ruleBlocked: ReadonlySet<string> = new Set(),
): LiftSummary {
  const robotCars = cars.filter((car) => car.use !== 'none');
  const guests = cars.length - robotCars.length;
  const access = (car: LiftRobotFacts) => columns.get(car.name)?.reading.robotAccess ?? 'unknown';
  const byRule = robotCars.filter((car) => ruleBlocked.has(car.name)).length;
  const counted = robotCars.filter((car) => !ruleBlocked.has(car.name));
  const open = counted.filter((car) => access(car) === 'allowed').length;
  const blocked = counted.filter((car) => access(car) === 'blocked').length;
  const unknown = counted.length - open - blocked;
  const facts: SummaryFact[] = [];
  if (byRule > 0) facts.push({ id: 'rule-blocked', label: `${byRule} blocked`, tone: 'warn' });
  if (blocked > 0) facts.push({ id: 'blocked', label: `${blocked} not available`, tone: 'warn' });
  if (unknown > 0) facts.push({ id: 'unknown', label: `${unknown} not known`, tone: 'quiet' });
  if (buildingKnown && guests > 0) {
    facts.push({ id: 'guests', label: plural(guests, 'guest lift'), tone: 'quiet' });
  }
  // The adapter declines every robot call during a fire, whatever each car
  // reads, so a count of open lifts would be false.
  if (fireAlarm) return { headline: 'No robots in the lifts: fire alarm', facts: [] };
  if (cars.length === 0) return { headline: 'No lifts reporting', facts };
  return {
    headline: buildingKnown
      ? `${open} of ${plural(robotCars.length, 'robot lift')} open`
      : `${open} of ${plural(robotCars.length, 'lift')} open to robots`,
    facts,
  };
}

// ----------------------------------------------------------------------------
// Doors.
// ----------------------------------------------------------------------------

export const NOT_HEARD = 'Not heard';

export interface DoorStateCell {
  readonly status: StatusKind;
  readonly label: string;
  readonly dashed: boolean;
}

export function doorStateCell(door: Pick<DoorViewModel, 'hasLiveState' | 'state'>): DoorStateCell {
  if (!door.hasLiveState) return { status: 'offline', label: NOT_HEARD, dashed: true };
  switch (door.state) {
    case 'open':
      return { status: 'active', label: 'Open', dashed: false };
    case 'closed':
      return { status: 'idle', label: 'Closed', dashed: false };
    case 'moving':
      return { status: 'moving', label: 'Moving', dashed: false };
    case 'fault':
      return { status: 'error', label: 'Fault', dashed: false };
    default:
      return { status: 'offline', label: 'Not known', dashed: false };
  }
}

export interface DoorControllerCell {
  readonly status: StatusKind;
  readonly label: string;
  /** For the sort: worst first. */
  readonly rank: number;
}

/** Three states, and the third is not a rounding of the second: unknown
 *  means the transport carries no such signal or its report has aged out,
 *  and one adapter outage must not read as a building of dead doors. Null
 *  when there is no health at all: an api-server with no such read, or a
 *  door whose adapter predates it, gets no chip. */
export function doorControllerCell(health: DoorHealth | undefined): DoorControllerCell | null {
  if (!health) return null;
  if (health.online === true) return { status: 'active', label: 'Online', rank: 2 };
  if (health.online === false) return { status: 'error', label: 'Not answering', rank: 0 };
  return { status: 'offline', label: 'Not known', rank: 1 };
}

/** "Opened for Phantas-3, 10 s ago". A request says what was asked, so a
 *  door that has not done it reads "Asked to open": that is the case an
 *  operator most needs to see. The stamp is the worker's, as the drawer's. */
export function doorLastChange(
  door: Pick<DoorViewModel, 'hasLiveState' | 'state' | 'lastCommand'>,
  audience: Audience,
  wallNowMs: number,
): string {
  const c = door.lastCommand;
  if (!door.hasLiveState || !c) return '—';
  const reached = c.requested === 'open' ? door.state === 'open' : door.state === 'closed';
  const verb = reached
    ? c.requested === 'open'
      ? 'Opened'
      : 'Closed'
    : c.requested === 'open'
      ? 'Asked to open'
      : 'Asked to close';
  let who: string;
  if (c.source === 'robot') {
    who = `for ${audience === 'ops' ? c.actor : robotName(c.actor)}`;
  } else if (c.source === 'console' && c.actor === 'console') {
    who = 'from the console';
  } else if (c.source === 'console' || audience === 'ops') {
    who = `by ${c.actor}`;
  } else {
    who = 'by another system';
  }
  return `${verb} ${who}, ${durationWords(wallNowMs - c.atMs)} ago`;
}

/**
 * Doors some door system says it owns. A door in the building map that no
 * adapter claims is not wired to anything, so its silence is expected: it
 * still has a row, and it raises nothing. With no gate reading to go by,
 * every door counts, because an unread gate says nothing.
 */
export function doorOwnedByAdapter(
  name: string,
  gates: readonly DoorWriteGate[] | null | undefined,
): boolean {
  if (!gates) return true;
  return gates.some((g) => Array.isArray(g.doors) && g.doors.includes(name));
}

export type RowTone = 'critical' | 'warning' | null;

export function doorTone(
  door: Pick<DoorViewModel, 'name' | 'hasLiveState' | 'state'>,
  health: DoorHealth | undefined,
  gates: readonly DoorWriteGate[] | null | undefined,
): RowTone {
  if (door.hasLiveState && door.state === 'fault') return 'critical';
  if (health?.online === false) return 'warning';
  if (!door.hasLiveState && doorOwnedByAdapter(door.name, gates)) return 'warning';
  return null;
}

export function liftTone(
  column: Pick<LiftColumnState, 'reading'> | undefined,
  robot: Pick<LiftRobotCell, 'stuck'>,
): RowTone {
  if (robot.stuck) return 'critical';
  return column?.reading.raisesItem ? 'warning' : null;
}

// ----------------------------------------------------------------------------
// Edge box.
// ----------------------------------------------------------------------------

const SERVICE_WORDS: Readonly<Record<string, string>> = {
  edge: 'Edge box',
  lift: 'Lift bridge',
  door: 'Door bridge',
  robot: 'Robot bridge',
};

/** A service this table has no word for keeps its own name, capitalised. */
export function gatewayName(service: string): string {
  return SERVICE_WORDS[service] ?? `${service.charAt(0).toUpperCase()}${service.slice(1)}`;
}

export interface GatewayStateCell {
  readonly status: StatusKind;
  readonly label: string;
  readonly tone: SummaryFact['tone'];
  /** For the sort: worst first. */
  readonly rank: number;
}

/**
 * Silence and a reported outage stay apart: `unreachable` means nothing has
 * been heard for 90 s (go check the box or its link), `down` means the
 * service said so (go check the service).
 */
export function gatewayStateCell(state: EdgeGateway['state']): GatewayStateCell {
  switch (state) {
    case 'up':
      return { status: 'active', label: 'Online', tone: 'ok', rank: 3 };
    case 'degraded':
      return { status: 'warning', label: 'Degraded', tone: 'warn', rank: 2 };
    case 'unreachable':
      return { status: 'warning', label: 'Unreachable', tone: 'warn', rank: 1 };
    default:
      return { status: 'error', label: 'Offline', tone: 'bad', rank: 0 };
  }
}

/** The version a box reports about itself. The health agent does not send
 *  one yet, so this is usually null and the screen says nothing. */
export function gatewayVersion(gateway: Pick<EdgeGateway, 'detail'>): string | null {
  const version = gateway.detail?.version;
  return typeof version === 'string' && version !== '' ? version : null;
}

/** The bridges, as the summary line says them. */
export function gatewayFacts(gateways: readonly EdgeGateway[]): SummaryFact[] {
  const version = gateways.map(gatewayVersion).find((v) => v !== null);
  const facts: SummaryFact[] = version
    ? [{ id: 'edge-version', label: `Edge box ${version}`, tone: 'quiet' }]
    : [];
  for (const g of [...gateways].sort((a, b) => a.service.localeCompare(b.service))) {
    const cell = gatewayStateCell(g.state);
    facts.push({
      id: `gateway-${g.service}`,
      label: `${gatewayName(g.service)} ${cell.label.toLowerCase()}`,
      tone: cell.tone,
    });
  }
  return facts;
}

// ----------------------------------------------------------------------------
// Needs attention.
// ----------------------------------------------------------------------------

/** Which control a card offers; the screen wires it up. */
export type DevicesAttentionTarget =
  | { readonly kind: 'lift'; readonly name: string; readonly recover: boolean }
  | { readonly kind: 'door'; readonly name: string }
  | { readonly kind: 'doors' }
  | { readonly kind: 'lift_gate'; readonly canArm: boolean }
  | { readonly kind: 'door_gate'; readonly canArm: boolean };

export interface DevicesAttention {
  readonly id: string;
  readonly severity: AttentionSeverity;
  readonly title: string;
  readonly detail?: string;
  readonly target: DevicesAttentionTarget;
}

export interface DevicesAttentionInput {
  readonly lifts: readonly Pick<LiftViewModel, 'name' | 'session'>[];
  readonly columns: ReadonlyMap<string, LiftCellColumn>;
  readonly doors: readonly Pick<DoorViewModel, 'name' | 'hasLiveState' | 'state'>[];
  readonly doorHealth: ReadonlyMap<string, DoorHealth> | null | undefined;
  readonly liftGate: LiftWriteGate | null | undefined;
  readonly doorGates: readonly DoorWriteGate[] | null | undefined;
  readonly wallNowMs: number;
}

/**
 * Stuck rides first, then the switches that stop every ride, then cars,
 * then doors. A guest car raises nothing (`LiftReading.raisesItem`): a card
 * about one would ask the front desk to manage the building's lifts.
 */
export function devicesAttention(input: DevicesAttentionInput): DevicesAttention[] {
  const { lifts, columns, doors, doorHealth, liftGate, doorGates, wallNowMs } = input;
  const stuck: DevicesAttention[] = [];
  const cars: DevicesAttention[] = [];
  for (const lift of lifts) {
    const column = columns.get(lift.name);
    if (!column) continue;
    const robot = liftRobotCell(lift, column, wallNowMs);
    if (robot.stuck) {
      const waiting = column.reading.kind === 'robot_waiting';
      stuck.push({
        id: `lift-${lift.name}-stuck`,
        severity: 'critical',
        title: `${lift.name}: ${robot.robot ?? 'A robot'} has ${
          waiting ? 'been waiting to board' : 'held the lift'
        } for ${durationWords(robot.ageMs ?? 0)}`,
        detail: 'Longer than a normal ride. Look at the car before you recover it.',
        target: { kind: 'lift', name: lift.name, recover: true },
      });
    } else if (column.reading.raisesItem) {
      cars.push({
        id: `lift-${lift.name}`,
        severity: 'warning',
        title:
          column.reading.kind === 'no_reading'
            ? `${lift.name}: not reporting`
            : `${lift.name}: ${column.reading.robots}`,
        detail: `Who acts: ${column.reading.whoActsLine}`,
        target: { kind: 'lift', name: lift.name, recover: false },
      });
    }
  }

  const switches: DevicesAttention[] = [];
  const liftGateStatus = writeGateStatus(liftGate);
  if (liftGateStatus.kind === 'disarmed') {
    switches.push({
      id: 'lift-gate',
      severity: 'warning',
      title: 'Robot lift rides are off',
      detail: 'Robots cannot call a lift until a supervisor arms this. A restart turns it off.',
      target: { kind: 'lift_gate', canArm: true },
    });
  } else if (liftGateStatus.kind === 'unvalidated') {
    switches.push({
      id: 'lift-gate',
      severity: 'warning',
      title: 'Robot lift rides cannot be turned on',
      detail: 'The lift system refuses every ride until its setup is fixed. Call Nexsoss.',
      target: { kind: 'lift_gate', canArm: false },
    });
  }
  for (const gate of doorGates ?? []) {
    const status = doorGateStatus(gate);
    // Shadow and stale are left out on purpose: a site can sit in shadow for
    // a long time by design, and a stale gate says nothing about the switch.
    if (status.kind !== 'disarmed' && status.kind !== 'unvalidated') continue;
    switches.push({
      id: `door-gate-${gate.adapter_id}`,
      severity: 'warning',
      title: status.kind === 'disarmed' ? 'Door control is off' : 'Door control cannot be turned on',
      detail:
        status.kind === 'disarmed'
          ? 'Nothing here can open a door until a supervisor arms this. Closing still works.'
          : 'The door system refuses to open any door until its setup is fixed. Call Nexsoss.',
      target: { kind: 'door_gate', canArm: doorArmBlockedReason(status) === undefined },
    });
  }

  const notHeard = doors.filter(
    (d) => !d.hasLiveState && doorOwnedByAdapter(d.name, doorGates),
  );
  const notAnswering = doors.filter(
    (d) => d.hasLiveState && doorHealth?.get(d.name)?.online === false,
  );
  const doorItems: DevicesAttention[] = [];
  if (notHeard.length === 1) {
    doorItems.push({
      id: 'doors-not-heard',
      severity: 'warning',
      title: `${notHeard[0]!.name}: not heard from`,
      detail: 'Nothing is reporting for this door.',
      target: { kind: 'door', name: notHeard[0]!.name },
    });
  } else if (notHeard.length > 1) {
    doorItems.push({
      id: 'doors-not-heard',
      severity: 'warning',
      title: `${notHeard.length} doors not heard from`,
      detail: notHeard.map((d) => d.name).join(', '),
      target: { kind: 'doors' },
    });
  }
  if (notAnswering.length === 1) {
    doorItems.push({
      id: 'doors-not-answering',
      severity: 'warning',
      title: `${notAnswering[0]!.name}: controller not answering`,
      target: { kind: 'door', name: notAnswering[0]!.name },
    });
  } else if (notAnswering.length > 1) {
    doorItems.push({
      id: 'doors-not-answering',
      severity: 'warning',
      title: `${notAnswering.length} door controllers not answering`,
      detail: notAnswering.map((d) => d.name).join(', '),
      target: { kind: 'doors' },
    });
  }
  return [...stuck, ...switches, ...cars, ...doorItems];
}
